import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import Icon from '../components/Icon';
import { userAPI, adminAPI } from '../services/api';
import { authAPI } from '../services/authApi';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTheme } from '../context/ThemeContext';
import { wp, hp, ms, vs, spacing, fontSize, iconSize } from '../utils/responsive';

const SubscriptionScreen = ({ navigation }) => {
  const { isDarkMode, toggleTheme, colors } = useTheme();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedTab, setSelectedTab] = useState('PLANS');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePlan, setActivePlan] = useState(null);
  const [userBalance, setUserBalance] = useState(0);

  // Helper function to check if subscription is expired
  const isSubscriptionExpired = (endDate) => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  };

  // Helper function to check if a plan is currently active
  const isPlanActive = (plan) => {
    if (!activePlan) return false;
    
    // Match by plan ID from the API response
    return (
      activePlan.id === plan.id || 
      activePlan.name === plan.name ||
      activePlan.rawData?.plan?._id === plan.id ||
      // Also check if plan names match (case insensitive)
      activePlan.name?.toLowerCase() === plan.name?.toLowerCase()
    );
  };

  // Helper function to check if user can afford a plan
  const canAffordPlan = (plan) => {
    return userBalance >= plan.price;
  };

  // Filter plans based on user preferences (show all plans)
  const getFilteredPlans = () => {
    // Show all plans sorted by price in ascending order
    return subscriptionPlans.sort((a, b) => a.price - b.price);
  };

  // Helper function to get subscription status with expiry check
  const getSubscriptionStatus = (subscription) => {
    if (!subscription) return 'INACTIVE';
    
    if (subscription.isActive === false) return 'INACTIVE';
    if (isSubscriptionExpired(subscription.endDate)) return 'EXPIRED';
    if (subscription.status) return subscription.status.toUpperCase();
    
    return 'ACTIVE';
  };

  // Fetch subscription plans and active plan on component mount
  useEffect(() => {
    // Set default plans immediately to avoid empty state
    setDefaultPlans();
    fetchSubscriptionData();
    fetchUserBalance();
  }, [selectedTab]);

  // Fetch user's wallet balance
  const fetchUserBalance = async () => {
    try {
      // Try to get balance from user profile first
      const response = await authAPI.getProfile();
      console.log('User profile response:', response);
      
      // Handle different response formats - look for balance in profile
      let balance = 0;
      if (response?.data?.balance !== undefined) {
        balance = response.data.balance;
      } else if (response?.balance !== undefined) {
        balance = response.balance;
      } else if (response?.wallet?.balance !== undefined) {
        balance = response.wallet.balance;
      } else {
        // Fallback: set balance to 0 if not found
        console.log('Balance not found in profile, setting to 0');
        balance = 0;
      }
      
      setUserBalance(parseFloat(balance) || 0);
      console.log('User balance set to:', balance);
    } catch (error) {
      console.error('Failed to fetch user balance:', error);
      // Set balance to 0 on error to prevent app crashes
      setUserBalance(0);
    }
  };

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      
      // Fetch active plan from API
      try {
        console.log('🔄 Fetching active subscription plan...');
        console.log('📡 API Endpoint:', '/user/subscriptions/active');
        
        const response = await userAPI.getActiveSubscription();
        console.log('✅ Active subscription API response:', response);
        
        // Handle the new API response structure
        if (response && response.success && response.data) {
          const subscriptionData = response.data;
          console.log('📋 Processing subscription data:', subscriptionData);
          
          // Check if subscription is approved/active
          if (subscriptionData.status === 'APPROVED') {
            const activeSubscription = {
              id: subscriptionData.plan._id,
              name: subscriptionData.plan.title,
              price: subscriptionData.plan.price,
              commission: subscriptionData.userCommissionPercentage,
              duration: '1 Month',
              description: subscriptionData.plan.description,
              startDate: subscriptionData.approvedAt || subscriptionData.createdAt,
              endDate: new Date(new Date(subscriptionData.approvedAt || subscriptionData.createdAt).getTime() + (30 * 24 * 60 * 60 * 1000)).toISOString(),
              status: 'active',
              planType: 'subscription',
              isActive: true,
              amount: subscriptionData.amount,
              rawData: subscriptionData
            };
            
            console.log('✅ Setting active subscription:', activeSubscription);
            setActivePlan(activeSubscription);
          } else {
            console.log('ℹ️ Subscription exists but not approved:', subscriptionData.status);
            setActivePlan(null);
          }
        } else {
          console.log('ℹ️ No active subscription found or invalid response');
          setActivePlan(null);
        }
      } catch (error) {
        console.log('❌ Active plan API error:', {
          status: error.response?.status,
          message: error.message,
          data: error.response?.data
        });
        
        if (error.response?.status === 404) {
          console.log('ℹ️ No active subscription plan found');
          setActivePlan(null);
        } else if (error.response?.status === 401) {
          console.log('🔐 Authentication required for active plan');
          Alert.alert(
            'Authentication Required',
            'Please login again to view your subscription.',
            [
              { text: 'OK', onPress: () => navigation.navigate('Login') }
            ]
          );
        } else if (error.response?.status === 403) {
          console.log('🚫 Access forbidden for active plan');
          Alert.alert('Access Denied', 'You don\'t have permission to view subscription data.');
        } else {
          console.log('⚠️ Error fetching active plan, continuing without it');
          setActivePlan(null);
        }
      }
      
      // Fetch subscription plans from admin API
      try {
        console.log('Fetching subscription plans from admin API...');
        const plansResponse = await adminAPI.getPlans();
        console.log('Admin plans response:', plansResponse);
        
        if (plansResponse && Array.isArray(plansResponse)) {
          // Transform admin plans to app format - show ALL plans
          const formattedPlans = plansResponse
            .map((plan, index) => ({
              id: plan._id || plan.id,
              name: plan.title, // Use 'title' field from API response
              price: plan.price,
              duration: '1 Month', // Default duration since not provided in API
              commission: plan.userCommissionPercentage, // Use the correct field name
              description: plan.description || '',
              features: [
                `Access to all markets`,
                `${plan.userCommissionPercentage}% commission rate`,
                `$${plan.price?.toLocaleString()} plan value`,
                plan.description || 'Premium trading features',
                'Email support',
                'Secure payment gateway'
              ],
              color: index === 0 ? '#4A90E2' : index === 1 ? '#00E676' : index === 2 ? '#FFD700' : '#FF9800',
              popular: index === 1, // Make second plan popular
              // Keep raw data for reference
              rawData: plan
            }));
          
          if (formattedPlans.length > 0) {
            console.log('Setting formatted plans:', formattedPlans);
            setSubscriptionPlans(formattedPlans);
          } else {
            console.log('No plans returned from API, using default plans');
            setDefaultPlans();
          }
        } else {
          console.log('Invalid plans response format, using defaults');
          setDefaultPlans();
        }
      } catch (error) {
        console.log('Error fetching admin plans:', {
          status: error.response?.status,
          message: error.message,
          data: error.response?.data
        });
        console.log('Using default plans as fallback');
        setDefaultPlans();
      }
      
    } catch (error) {
      console.error('Error in fetchSubscriptionData:', error);
      Alert.alert('Error', 'Failed to load subscription data. Please try again.');
      setDefaultPlans();
    } finally {
      setLoading(false);
    }
  };
  
  const setDefaultPlans = () => {
    const defaultPlans = [
      {
        id: 1,
        name: 'Basic Plan',
        price: 1000,
        duration: '1 Month',
        features: [
          'Access to all markets',
          'Basic charts & indicators',
          'Email support',
          '$1000 wallet credit',
        ],
        color: '#4A90E2',
        popular: false,
      },
      {
        id: 2,
        name: 'Premium Plan',
        price: 5000,
        duration: '3 Months',
        features: [
          'All Basic features',
          'Advanced technical indicators',
          'Real-time market data',
          'Priority support',
          '$5000 wallet credit',
          '10% bonus credit',
        ],
        color: '#00E676',
        popular: true,
      },
      {
        id: 3,
        name: 'Professional Plan',
        price: 15000,
        duration: '1 Year',
        features: [
          'All Premium features',
          'AI-powered trading signals',
          'Dedicated account manager',
          'VIP customer support',
          '$15000 wallet credit',
          '20% bonus credit',
          'Free market analysis',
        ],
        color: '#FFD700',
        popular: false,
      },
    ];
    
    setSubscriptionPlans(defaultPlans);
  };
  


  const handleSubscribe = async (plan) => {
    console.log('Plan selected:', plan);
    console.log('User balance:', userBalance);
    console.log('Plan price:', plan.price);
    
    // Check if user already has an active plan
    if (activePlan) {
      Alert.alert(
        'Active Subscription',
        `You already have an active ${activePlan.name} subscription.\n\nExpires: ${new Date(activePlan.endDate).toLocaleDateString()}\n\nOnly one plan can be active at a time. Would you like to upgrade?`,
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Upgrade', onPress: () => {
            if (userBalance >= plan.price) {
              processPayment(plan);
            } else {
              const shortfall = plan.price - userBalance;
              Alert.alert(
                'Insufficient Balance',
                `You need $${shortfall.toFixed(2)} more to upgrade to this plan.\n\nYour Balance: $${userBalance.toFixed(2)}\nPlan Price: $${plan.price}`,
                [{ text: 'OK' }]
              );
            }
          }}
        ]
      );
      return;
    }
    
    // Check if user has sufficient balance for new plan
    if (userBalance >= plan.price) {
      // User has sufficient balance - auto-activate plan
      Alert.alert(
        'Activate Plan',
        `You have sufficient balance ($${userBalance.toFixed(2)}) to activate this plan.\n\nPlan: ${plan.name}\nPrice: $${plan.price}\n\nDo you want to activate it now?`,
        [
          {
            text: 'Cancel',
            style: 'cancel'
          },
          {
            text: 'Activate Plan',
            onPress: () => processPayment(plan)
          }
        ]
      );
    } else {
      // Insufficient balance
      const shortfall = plan.price - userBalance;
      Alert.alert(
        'Insufficient Balance',
        `You need $${shortfall.toFixed(2)} more to activate this plan.\n\nYour Balance: $${userBalance.toFixed(2)}\nPlan Price: $${plan.price}\n\nPlease add funds to your wallet first.`,
        [
          {
            text: 'OK',
            style: 'default'
          },
          {
            text: 'Add Funds',
            onPress: () => navigation.navigate('Wallet')
          }
        ]
      );
    }
  };

  const processPayment = async (plan) => {
    try {
      setIsProcessingPayment(true);
      
      console.log('Processing payment for plan:', {
        id: plan.id,
        name: plan.name,
        price: plan.price,
        commission: plan.commission
      });
      
      // Call the subscription purchase API (auto-approves if sufficient balance)
      const response = await userAPI.purchaseSubscription(plan.id);
      console.log('Purchase subscription response:', response);
      
      // Refresh user balance and active plan data
      await fetchUserBalance();
      await fetchSubscriptionData();
      
      setIsProcessingPayment(false);
      
      Alert.alert(
        '🎉 Plan Activated Successfully!',
        `Your subscription is now active:\n\n📦 Plan: ${plan.name}\n💰 Amount: $${plan.price?.toLocaleString()}${plan.commission ? `\n📈 Commission: ${plan.commission}%` : ''}\n\n🚀 You can now start trading with full access!`,
        [
          {
            text: '🏠 Go to Dashboard',
            onPress: () => navigation.replace('MainTabs'),
          },
          {
            text: '👀 View Active Plan',
            onPress: () => {
              setSelectedTab('ACTIVE');
            }
          }
        ]
      );
    } catch (error) {
      console.error('Payment processing error:', error);
      setIsProcessingPayment(false);
      
      let errorMessage = 'Plan activation failed. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid plan selection or insufficient balance.';
      } else if (error.response?.status === 409) {
        errorMessage = 'You already have an active subscription. Only one plan can be active at a time.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      Alert.alert(
        'Activation Failed',
        errorMessage,
        [{ text: 'OK' }]
      );
    }
  };

  const handleSkip = () => {
    navigation.goBack();
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton} 
          onPress={() => navigation.goBack()}
        >
          <Icon name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>AI Bot Subscription</Text>
        <View style={styles.placeholder} />
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#4A90E2" />
          <Text style={styles.loadingText}>Loading AI bot subscription...</Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.scrollContent}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerContent}>
              <Icon name="diamond" size={48} color="#4A90E2" />
              <Text style={styles.headerTitle}>Choose Your Plan</Text>
              <Text style={styles.headerSubtitle}>
                Select a subscription and get instant wallet credit
              </Text>
              
              {/* Wallet Balance Display */}
              <View style={styles.balanceContainer}>
                <Icon name="wallet" size={20} color="#00E676" />
                <Text style={styles.balanceText}>
                  Wallet Balance: ${userBalance.toFixed(2)}
                </Text>
              </View>
              
              {/* Active Plan Status */}
              {activePlan && (
                <View style={styles.activePlanBanner}>
                  <Icon name="checkmark-circle" size={20} color="#00E676" />
                  <Text style={styles.activePlanText}>
                    Active: {activePlan.name}
                  </Text>
                </View>
              )}
            </View>
          </View>

          {/* Tabs */}
          <View style={styles.tabsContainer}>
            {['PLANS', 'ACTIVE'].map((tab) => (
              <TouchableOpacity
                key={tab}
                style={[
                  styles.tab,
                  selectedTab === tab && styles.activeTab
                ]}
                onPress={() => setSelectedTab(tab)}
              >
                <Text
                  style={[
                    styles.tabText,
                    selectedTab === tab && styles.activeTabText
                  ]}
                >
                  {tab}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Tab Content */}
          {selectedTab === 'PLANS' && (
            <View style={styles.plansContainer}>
              {getFilteredPlans().length > 0 ? (
                getFilteredPlans().map((plan) => {
                  const isAffordable = canAffordPlan(plan);
                  const isActive = isPlanActive(plan);
                  
                  return (
                <TouchableOpacity
                key={plan.id}
                style={[
                  styles.planCard,
                  selectedPlan?.id === plan.id && styles.selectedPlanCard,
                  { borderColor: plan.color },
                  selectedPlan?.id === plan.id && { borderColor: plan.color, borderWidth: 3 }
                  // Remove opacity and border changes for unaffordable plans
                ]}
                onPress={() => setSelectedPlan(plan)}
                disabled={false} // Allow all plans to be clickable
              >
                {isPlanActive(plan) && (
                  <View style={[styles.activeBadge, { backgroundColor: '#00E676' }]}>
                    <Icon name="checkmark-circle" size={12} color="#fff" />
                    <Text style={styles.activeText}>ACTIVE</Text>
                  </View>
                )}

                {selectedPlan?.id === plan.id && !isPlanActive(plan) && (
                  <View style={[styles.selectedBadge, { backgroundColor: plan.color }]}>
                    <Icon name="checkmark" size={12} color="#fff" />
                    <Text style={styles.selectedText}>SELECTED</Text>
                  </View>
                )}

                <View style={styles.planHeader}>
                  <Text style={styles.planName}>{plan.name}</Text>
                  <View style={styles.priceContainer}>
                    <Text style={styles.currency}>$</Text>
                    <Text style={styles.price}>{plan.price}</Text>
                  </View>
                  <Text style={styles.duration}>{plan.duration}</Text>
                  {plan.commission && (
                    <View style={styles.commissionBadge}>
                      <Text style={styles.commissionText}>{plan.commission}% Commission</Text>
                    </View>
                  )}
                </View>

                <View style={styles.divider} />

                <View style={styles.featuresContainer}>
                  {plan.features.map((feature, index) => (
                    <View key={index} style={styles.featureRow}>
                      <Icon
                        name="checkmark-circle"
                        size={18}
                        color={plan.color}
                      />
                      <Text style={styles.featureText}>{feature}</Text>
                    </View>
                  ))}
                </View>

                <TouchableOpacity
                  style={[
                    styles.selectButton,
                    selectedPlan?.id === plan.id && {
                      backgroundColor: plan.color,
                    },
                    isProcessingPayment && {
                      opacity: 0.7,
                    },
                  ]}
                  onPress={() => !isProcessingPayment && handleSubscribe(plan)}
                  disabled={isProcessingPayment}
                >
                  {isProcessingPayment && selectedPlan?.id === plan.id ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text
                      style={[
                        styles.selectButtonText,
                        selectedPlan?.id === plan.id && styles.selectedButtonText,
                      ]}
                    >
                      {isPlanActive(plan)
                        ? 'Current Plan'
                        : selectedPlan?.id === plan.id 
                        ? (canAffordPlan(plan)
                          ? (activePlan ? 'Activate Upgrade' : 'Activate Plan')
                          : `Need $${(plan.price - userBalance).toFixed(0)} More`)
                        : (activePlan ? 'Upgrade Plan' : 'Select Plan')
                      }
                    </Text>
                  )}
                </TouchableOpacity>
              </TouchableOpacity>
                )
              })) : (
                <View style={styles.emptyPlansContainer}>
                  <Text style={styles.emptyPlansText}>Loading plans...</Text>
                </View>
              )}
            </View>
          )}
          
          {/* Active Plan Tab */}
          {selectedTab === 'ACTIVE' && (
            <View style={styles.activeContainer}>
              {activePlan ? (
                <View style={styles.activePlanCard}>
                  <Icon 
                    name={getSubscriptionStatus(activePlan) === 'ACTIVE' ? "checkmark-circle" : 
                          getSubscriptionStatus(activePlan) === 'EXPIRED' ? "close-circle" : "time-outline"} 
                    size={48} 
                    color={getSubscriptionStatus(activePlan) === 'ACTIVE' ? "#00E676" : 
                           getSubscriptionStatus(activePlan) === 'EXPIRED' ? "#FF5252" : "#FFD700"} 
                  />
                  <Text style={styles.activePlanTitle}>{activePlan.name}</Text>
                  <Text style={styles.activePlanPrice}>${activePlan.price?.toLocaleString() || 'N/A'}</Text>
                  
                  {/* Status Badge */}
                  <View style={[
                    styles.statusBadge, 
                    { backgroundColor: getSubscriptionStatus(activePlan) === 'ACTIVE' ? '#00E676' : 
                                       getSubscriptionStatus(activePlan) === 'EXPIRED' ? '#FF5252' : '#FFD700' }
                  ]}>
                    <Text style={styles.statusText}>
                      {getSubscriptionStatus(activePlan)}
                    </Text>
                  </View>
                  
                  {/* Plan Details */}
                  <View style={styles.planDetailsContainer}>
                    {activePlan.commission && (
                      <View style={styles.detailRow}>
                        <Icon name="trending-up" size={16} color="#FFD700" />
                        <Text style={styles.detailLabel}>Commission:</Text>
                        <Text style={styles.detailValue}>{activePlan.commission}%</Text>
                      </View>
                    )}
                    
                    {activePlan.duration && (
                      <View style={styles.detailRow}>
                        <Icon name="time" size={16} color="#4A90E2" />
                        <Text style={styles.detailLabel}>Duration:</Text>
                        <Text style={styles.detailValue}>{activePlan.duration}</Text>
                      </View>
                    )}
                    
                    {activePlan.startDate && (
                      <View style={styles.detailRow}>
                        <Icon name="play-circle" size={16} color="#00E676" />
                        <Text style={styles.detailLabel}>Started:</Text>
                        <Text style={styles.detailValue}>
                          {new Date(activePlan.startDate).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short', 
                            year: 'numeric'
                          })}
                        </Text>
                      </View>
                    )}
                    
                    {activePlan.endDate && (
                      <View style={styles.detailRow}>
                        <Icon name="calendar" size={16} color={activePlan.isActive !== false ? "#E5E7EB" : "#FF5252"} />
                        <Text style={styles.detailLabel}>Expires:</Text>
                        <Text style={[styles.detailValue, {
                          color: new Date(activePlan.endDate) < new Date() ? '#FF5252' : '#E5E7EB'
                        }]}>
                          {new Date(activePlan.endDate).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </Text>
                      </View>
                    )}
                  </View>
                  
                  {/* Action Buttons */}
                  <View style={styles.actionButtonsContainer}>
                    <TouchableOpacity 
                      style={styles.refreshButton}
                      onPress={() => fetchSubscriptionData()}
                    >
                      <Icon name="refresh" size={20} color="#4A90E2" />
                      <Text style={styles.refreshButtonText}>Refresh</Text>
                    </TouchableOpacity>
                    
                    <TouchableOpacity 
                      style={styles.upgradePlanButton}
                      onPress={() => setSelectedTab('PLANS')}
                    >
                      <Icon name="arrow-up-circle" size={20} color="#FFD700" />
                      <Text style={styles.upgradePlanText}>Upgrade</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <View style={styles.noActiveContainer}>
                  <Icon name="alert-circle-outline" size={48} color="#9CA3AF" />
                  <Text style={styles.noActiveText}>No Active Subscription</Text>
                  <Text style={styles.noActiveSubtext}>Choose a plan to get started</Text>
                  <TouchableOpacity 
                    style={styles.browsePlansButton}
                    onPress={() => setSelectedTab('PLANS')}
                  >
                    <Text style={styles.browsePlansText}>Browse Plans</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          )}
          


          {selectedTab === 'PLANS' && (
            <View style={styles.benefitsContainer}>
            <Text style={styles.benefitsTitle}>Why Subscribe?</Text>
            <View style={styles.benefitRow}>
              <Icon name="flash" size={24} color="#FFD700" />
              <Text style={styles.benefitText}>Instant wallet credit</Text>
            </View>
            <View style={styles.benefitRow}>
              <Icon name="shield-checkmark" size={24} color="#00E676" />
              <Text style={styles.benefitText}>Secure payment gateway</Text>
            </View>
            <View style={styles.benefitRow}>
              <Icon name="gift" size={24} color="#FF5252" />
              <Text style={styles.benefitText}>Bonus credits on premium plans</Text>
            </View>
            <View style={styles.benefitRow}>
              <Icon name="trending-up" size={24} color="#4A90E2" />
              <Text style={styles.benefitText}>Advanced trading features</Text>
            </View>
            </View>
          )}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0A0E27',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 50,
    paddingHorizontal: 20,
    paddingBottom: 20,
    backgroundColor: '#0A0E27',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#1A1F3A',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '600',
  },
  placeholder: {
    width: 40,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0A0E27',
  },
  loadingText: {
    color: '#9CA3AF',
    fontSize: 16,
    marginTop: 16,
  },
  scrollContent: {
    paddingBottom: 30,
  },
  header: {
    paddingTop: 50,
    paddingBottom: 30,
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  headerContent: {
    alignItems: 'center',
  },
  activePlanBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1A2332',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginTop: 16,
    borderWidth: 1,
    borderColor: '#00E676',
  },
  activePlanText: {
    color: '#00E676',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 28,
    fontWeight: '700',
    marginTop: 16,
    marginBottom: 8,
  },
  headerSubtitle: {
    color: '#9CA3AF',
    fontSize: 14,
    textAlign: 'center',
  },
  balanceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 230, 118, 0.1)',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginTop: 12,
    borderWidth: 1,
    borderColor: 'rgba(0, 230, 118, 0.3)',
  },
  balanceText: {
    color: '#00E676',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#1A1F3A',
    marginHorizontal: 20,
    marginBottom: 20,
    borderRadius: 12,
    padding: 4,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderRadius: 8,
  },
  activeTab: {
    backgroundColor: '#4A90E2',
  },
  tabText: {
    color: '#9CA3AF',
    fontSize: 14,
    fontWeight: '600',
  },
  activeTabText: {
    color: '#FFFFFF',
  },
  plansContainer: {
    paddingHorizontal: 16,
  },
  emptyPlansContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyPlansText: {
    color: '#9CA3AF',
    fontSize: 16,
    textAlign: 'center',
  },
  planCard: {
    backgroundColor: '#1A1F3A',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  selectedPlanCard: {
    borderWidth: 2,
    ...(Platform.OS === 'web' 
      ? {
          boxShadow: '0px 4px 8px rgba(74, 144, 226, 0.3)',
        }
      : {
          shadowColor: '#4A90E2',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.3,
          shadowRadius: 8,
          elevation: 8,
        }
    ),
  },
  popularBadge: {
    position: 'absolute',
    top: -10,
    right: 20,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  popularText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
    marginLeft: 4,
  },
  selectedBadge: {
    position: 'absolute',
    top: -10,
    left: 20,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  selectedText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
    marginLeft: 4,
  },
  activeBadge: {
    position: 'absolute',
    top: -10,
    right: 20,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  activeText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
    marginLeft: 4,
  },
  notAffordableBadge: {
    position: 'absolute',
    top: -10,
    left: 20,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  notAffordableText: {
    color: '#fff',
    fontSize: 9,
    fontWeight: '700',
    marginLeft: 4,
  },
  planHeader: {
    alignItems: 'center',
    marginBottom: 20,
  },
  planName: {
    color: '#fff',
    fontSize: 22,
    fontWeight: '700',
    marginBottom: 12,
  },
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  currency: {
    color: '#4A90E2',
    fontSize: 20,
    fontWeight: '600',
    marginTop: 4,
  },
  price: {
    color: '#fff',
    fontSize: 40,
    fontWeight: '700',
  },
  duration: {
    color: '#9CA3AF',
    fontSize: 14,
    fontWeight: '500',
  },
  commissionBadge: {
    backgroundColor: '#FFD700',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    marginTop: 8,
  },
  commissionText: {
    color: '#000',
    fontSize: 12,
    fontWeight: '600',
  },
  divider: {
    height: 1,
    backgroundColor: '#2A2F4A',
    marginVertical: 16,
  },
  featuresContainer: {
    marginBottom: 20,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  featureText: {
    color: '#E5E7EB',
    fontSize: 14,
    marginLeft: 12,
    flex: 1,
  },
  selectButton: {
    backgroundColor: '#374151',
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  selectButtonText: {
    color: '#9CA3AF',
    fontSize: 16,
    fontWeight: '600',
  },
  selectedButtonText: {
    color: '#FFFFFF',
  },
  benefitsContainer: {
    backgroundColor: '#1A1F3A',
    margin: 20,
    padding: 20,
    borderRadius: 16,
  },
  benefitsTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 16,
    textAlign: 'center',
  },
  benefitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  benefitText: {
    color: '#E5E7EB',
    fontSize: 14,
  },
  // Active Plan Styles
  activeContainer: {
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  activePlanCard: {
    backgroundColor: '#1A1F3A',
    borderRadius: 16,
    padding: 30,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#00E676',
  },
  activePlanTitle: {
    color: '#fff',
    fontSize: 24,
    fontWeight: '700',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  activePlanPrice: {
    color: '#00E676',
    fontSize: 32,
    fontWeight: '700',
    marginBottom: 16,
  },
  statusBadge: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 20,
    marginBottom: 24,
  },
  statusText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 1,
  },
  planDetailsContainer: {
    width: '100%',
    backgroundColor: '#0F1419',
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  detailLabel: {
    color: '#9CA3AF',
    fontSize: 14,
    flex: 1,
  },
  detailValue: {
    color: '#E5E7EB',
    fontSize: 14,
    fontWeight: '600',
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
    justifyContent: 'center',
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#374151',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    gap: 6,
  },
  refreshButtonText: {
    color: '#4A90E2',
    fontSize: 14,
    fontWeight: '600',
  },
  upgradePlanButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1A2332',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FFD700',
    gap: 6,
  },
  upgradePlanText: {
    color: '#FFD700',
    fontSize: 14,
    fontWeight: '600',
  },
  noActiveContainer: {
    backgroundColor: '#1A1F3A',
    borderRadius: 16,
    padding: 40,
    alignItems: 'center',
  },
  noActiveText: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  noActiveSubtext: {
    color: '#9CA3AF',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 20,
  },
  browsePlansButton: {
    backgroundColor: '#4A90E2',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 8,
  },
  browsePlansText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default SubscriptionScreen;
